import React, { useEffect } from 'react';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Menu from '@material-ui/core/Menu';
import { Box, Button } from '@material-ui/core';

/**
 * Interface pour les props du composant EditableCell
 */
interface Props {
  content: React.ReactElement;
  className?: string;
  closeCell?: boolean;
  onClosed?: () => void;
}

/**
 * Composant de cellule éditable avec menu déroulant
 * 
 * Ce composant :
 * - Affiche un contenu avec une icône de flèche pour indiquer l'éditabilité
 * - Ouvre un menu déroulant au clic pour permettre l'édition
 * - Peut être fermé programmatiquement via la prop closeCell
 * - Inclut un bouton de fermeture et un message informatif
 * 
 * Logique métier :
 * - Gestion de l'état d'ouverture/fermeture du menu
 * - Prévention de la propagation des événements de clic
 * - Fermeture automatique si closeCell est true
 * - Interface utilisateur intuitive avec feedback visuel
 * 
 * @param content Contenu à afficher dans la cellule
 * @param className Classe CSS optionnelle
 * @param closeCell Force la fermeture du menu (optionnel)
 * @param onClosed Callback appelé lors de la fermeture (optionnel)
 * @param children Contenu du menu déroulant
 * @returns Composant React de cellule éditable
 */
export const EditableCell: React.FC<Props> = ({
  className,
  content,
  children,
  closeCell,
  onClosed
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if(closeCell && closeCell === true) {
      setAnchorEl(null);
      onClosed && onClosed();
    }
  },[closeCell,onClosed]);

  const handleClose = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <Box
      display={'flex'}
      className={className}
      alignItems={'center'}
      onClick={handleClick}
    >
      {content}
      <KeyboardArrowDownIcon
        aria-controls="simple-menu"
        aria-haspopup="true"
        className="opaqueOnHover iconButton"
      />
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: '250px',
            width: '45ch',
          },
        }}
      >
        <Box p={1} display={'flex'} flexDirection={'column'}>
          {children}
          &nbsp;
          <Button variant="contained" color="primary" onClick={handleClose}>
            Fermer
          </Button>
          &nbsp;
          <i>
            Veuillez valider les changements pour voir les nouveaux taux
            d'occupation
          </i>
        </Box>
      </Menu>
    </Box>
  );
};
